import { useState, useEffect, useCallback, useMemo } from "react";
import { Row, Col, Card, Button, Spin, Empty, Typography, Tag } from "antd";
import { ShoppingCartOutlined, PictureOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "../../../shared/styles/HomeShop.css";
import "../../../shared/styles/ErpPages.css";
import { formatCurrency } from "../../../utils/formatters";
import { getStorageUrl } from "../../../shared/api/AllApi";
import { useProductsCatalog } from "../../../hooks/useCatalogData";
import { parseLocalizedName } from "../../../utils/localization";
import ListSearch from "../../../shared/ui/ListSearch";

const { Text } = Typography;

function ProductImage({ imageUrl, alt }) {
  const [error, setError] = useState(false);
  const hasUrl = Boolean(imageUrl && String(imageUrl).trim());

  useEffect(() => {
    setError(false);
  }, [imageUrl]);

  if (!hasUrl || error) {
    return (
      <div className="hs-image hs-image--empty" aria-hidden>
        <PictureOutlined />
      </div>
    );
  }

  return (
    <div className="hs-image">
      <img
        src={getStorageUrl(imageUrl)}
        alt={alt}
        onError={() => setError(true)}
      />
    </div>
  );
}

const HomeShop = ({ handleClick }) => {
  const { t, i18n } = useTranslation();
  const [nameFilter, setNameFilter] = useState("");
  const { data: dataSource, loading } = useProductsCatalog();

  const filteredData = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    return (dataSource || [])
      .map((item) => ({
        ...item,
        displayName: parseLocalizedName(item.name, i18n.language, "Unknown"),
      }))
      .filter((item) => {
        if (!q) return true;
        return (
          String(item.displayName).toLowerCase().includes(q) ||
          String(item.sku || "").toLowerCase().includes(q) ||
          String(item.id).includes(q)
        );
      })
      .sort((a, b) => (a.stock || 0) - (b.stock || 0));
  }, [dataSource, nameFilter, i18n.language]);

  const inStockCount = useMemo(
    () => filteredData.filter((p) => Number(p.stock) > 0).length,
    [filteredData]
  );

  const onAdd = useCallback(
    (item) => {
      if (Number(item.stock) > 0) handleClick?.(item);
    },
    [handleClick]
  );

  return (
    <div className="erp-page home-shop-page">
      <div className="erp-shell">
        <div className="erp-header">
          <div>
            <div className="erp-breadcrumb">
              {t("sales", "Sales")} / {t("allProducts", "Products")}
            </div>
            <h1>{t("allProducts", "All products")}</h1>
          </div>
        </div>

        <div className="erp-meta">
          <span>
            {t("records", "Records")}: <b>{filteredData.length}</b>
          </span>
          <span>
            {t("inStock", "In stock")}: <b>{inStockCount}</b>
          </span>
        </div>

        <div className="erp-toolbar">
          <ListSearch
            value={nameFilter}
            onChange={setNameFilter}
            placeholder={t("searchPlaceholder")}
            width={320}
          />
        </div>

        <div className="erp-body home-shop-body">
          {loading ? (
            <div className="home-shop-loading">
              <Spin size="large" />
            </div>
          ) : filteredData.length === 0 ? (
            <Empty
              description={
                nameFilter
                  ? t("noResults", "No products match your search")
                  : t("noProducts", "No products yet")
              }
            />
          ) : (
            <Row gutter={[12, 12]}>
              {filteredData.map((item) => {
                const outOfStock = Number(item.stock) <= 0;
                return (
                  <Col xs={24} sm={12} md={8} lg={6} key={item.id}>
                    <Card
                      className={`hs-card${outOfStock ? " hs-card--oos" : ""}`}
                      size="small"
                      cover={<ProductImage imageUrl={item.image_url} alt={item.displayName} />}
                    >
                      <div className="hs-card-name" title={item.displayName}>
                        {item.displayName}
                      </div>
                      <div className="hs-card-price">{formatCurrency(item.price)}</div>
                      <div className="hs-card-meta">
                        <Text type="secondary">
                          {t("sku")}: {item.sku || "—"}
                        </Text>
                        <Tag color={outOfStock ? "error" : "default"}>
                          {t("stock")}: {item.stock}
                        </Tag>
                      </div>
                      <Button
                        type="primary"
                        block
                        icon={<ShoppingCartOutlined />}
                        disabled={outOfStock}
                        onClick={() => onAdd(item)}
                      >
                        {outOfStock ? t("outOfStock") : t("addToCart")}
                      </Button>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeShop;
